import i18n from '@/legacy/i18n';

/**
 * Reserved keys the module constructs itself (fallback destinations). Never part of the
 * backend-derived whitelist — gating them would be circular.
 */
const RESERVED_KEYS = new Set(['unexpected_error', 'network_error', 'timeout']);

const isReservedKey = (key: string): boolean => RESERVED_KEYS.has(key) || /^http_\d+$/.test(key);

interface ContractCache {
	/** Whitelisted (non-reserved) backend-derived keys. */
	readonly keys: ReadonlySet<string>;
	/** Every key in the bundle, including reserved ones — used for fallback-key lookups. */
	readonly allKeys: ReadonlySet<string>;
}

let cachedContract: ContractCache | undefined;

/**
 * Runtime-derived key contract: the `errors` namespace bundle for the source-of-truth
 * language (`es`), minus reserved keys. Memoized lazily on first call — never at module
 * top level, since import order relative to `shared/i18n/bootstrap.ts` is not guaranteed.
 */
const loadContract = (): ContractCache => {
	if (cachedContract) return cachedContract;

	const bundle: Record<string, unknown> | undefined | null = i18n.getResourceBundle('es', 'errors');

	const keys = bundle ? Object.keys(bundle) : [];
	cachedContract = {
		keys: new Set(keys.filter((key) => !isReservedKey(key))),
		allKeys: new Set(keys),
	};
	return cachedContract;
};

export const getErrorKeyContract = (): ReadonlySet<string> => loadContract().keys;

export const isContractKey = (key: string | undefined): key is string =>
	typeof key === 'string' && key.length > 0 && getErrorKeyContract().has(key);

/** True when `key` exists anywhere in the bundle (whitelisted or reserved). */
export const hasBundleKey = (key: string): boolean => loadContract().allKeys.has(key);

/**
 * Translates a resolved error key (contract key, legacy key, or reserved fallback key)
 * to display-ready text from the `errors` namespace bundle.
 *
 * `keySeparator: false` is mandatory (ADR-1): contract keys are stored as flat literal
 * strings containing dots (`dominio.recurso.causa`). Without this override, i18next's
 * default `keySeparator: '.'` treats the string as a nested-object path instead of an
 * exact leaf lookup, which can silently render `Missing: <key>` to the user.
 *
 * `params` is optional interpolation data (D1 — the `detail` allow-list in
 * `detailPolicy.ts` is the only caller that ever passes it).
 */
export const translateErrorKey = (key: string, params?: Record<string, string>): string => {
	// `key` is a runtime-derived `string`, not a literal member of the generated `errors`
	// namespace key union (`shared/i18n/i18next.d.ts`), so it can't satisfy `TFunction`'s
	// typed overload for a namespaced call. Narrow, intentional, permanent cast — not tied
	// to a P1->P2 bridge or any pending i18n migration step.
	return String(i18n.t(key, { ns: 'errors', keySeparator: false, ...params } as never));
};

/**
 * Hierarchical `dominio.recurso` fallback (ADR-2, item 9). Walks `key`'s dotted prefix
 * chain longest-first, returning the first ancestor (or `key` itself) present on the
 * whitelist. Reuses `isContractKey` (not `hasBundleKey`) so a backend domain literally
 * named e.g. `timeout` can never collide with the reserved `timeout` fallback key.
 *
 * Ships inert until the catalog migration (Batch B) supplies dotted keys and their
 * `dominio`-level generic entries — expected, not a bug.
 */
export const resolveDisplayKey = (key: string | undefined): string | undefined => {
	if (typeof key !== 'string' || key.length === 0) return undefined;
	const segments = key.split('.');
	for (let end = segments.length; end >= 1; end--) {
		const candidate = segments.slice(0, end).join('.');
		if (isContractKey(candidate)) return candidate;
	}
	return undefined;
};

/** Test-only: clears the lazy memoization so each test can control the resource bundle. */
export const resetErrorKeyContract = (): void => {
	cachedContract = undefined;
};
